export type StatsigConsoleLogLevel =
  | 'debug'
  | 'info'
  | 'log'
  | 'warn'
  | 'error';

export interface StatsigConsoleLogCaptureSettings {
  enabled: true;
  logLevel: StatsigConsoleLogLevel;
  sampleRate: number;
  maxKeys: number;
  maxDepth: number;
  maxStringLength: number;
}

type EnvSource = Record<string, string | undefined>;

const DEFAULT_STATSIG_CONSOLE_LOG_CAPTURE_SETTINGS: Omit<
  StatsigConsoleLogCaptureSettings,
  'enabled'
> = {
  logLevel: 'warn',
  sampleRate: 1,
  maxKeys: 50,
  maxDepth: 3,
  maxStringLength: 2000,
};

const STATSIG_CONSOLE_LOG_LEVELS: StatsigConsoleLogLevel[] = [
  'debug',
  'info',
  'log',
  'warn',
  'error',
];

const parseOptionalBoolean = (
  value: string | undefined,
  envName: string,
): boolean | undefined => {
  if (value == null || value.trim() === '') {
    return undefined;
  }

  const normalizedValue = value.trim().toLowerCase();

  if (normalizedValue === 'true') {
    return true;
  }

  if (normalizedValue === 'false') {
    return false;
  }

  throw new Error(
    `${envName} must be "true" or "false" when provided. Received "${value}".`,
  );
};

const parseOptionalNumber = (
  value: string | undefined,
  envName: string,
): number | undefined => {
  if (value == null || value.trim() === '') {
    return undefined;
  }

  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue)) {
    throw new Error(
      `${envName} must be a finite number when provided. Received "${value}".`,
    );
  }

  return parsedValue;
};

const parseOptionalPositiveInteger = (
  value: string | undefined,
  envName: string,
): number | undefined => {
  const parsedValue = parseOptionalNumber(value, envName);

  if (parsedValue == null) {
    return undefined;
  }

  if (!Number.isInteger(parsedValue) || parsedValue <= 0) {
    throw new Error(
      `${envName} must be a positive integer when provided. Received "${value}".`,
    );
  }

  return parsedValue;
};

const parseOptionalConsoleLogLevel = (
  value: string | undefined,
): StatsigConsoleLogLevel | undefined => {
  if (value == null || value.trim() === '') {
    return undefined;
  }

  const normalizedValue = value.trim().toLowerCase();

  if (
    STATSIG_CONSOLE_LOG_LEVELS.includes(
      normalizedValue as StatsigConsoleLogLevel,
    )
  ) {
    return normalizedValue as StatsigConsoleLogLevel;
  }

  throw new Error(
    'REACT_APP_STATSIG_CONSOLE_LOG_LEVEL must be one of ' +
      `"${STATSIG_CONSOLE_LOG_LEVELS.join('", "')}". Received "${value}".`,
  );
};

export const getStatsigConsoleLogCaptureSettings = (
  env: EnvSource,
): StatsigConsoleLogCaptureSettings | undefined => {
  const consoleLogCaptureEnabled =
    parseOptionalBoolean(
      env.REACT_APP_STATSIG_CONSOLE_LOG_CAPTURE_ENABLED,
      'REACT_APP_STATSIG_CONSOLE_LOG_CAPTURE_ENABLED',
    ) ?? false;

  if (!consoleLogCaptureEnabled) {
    return undefined;
  }

  const sampleRate = parseOptionalNumber(
    env.REACT_APP_STATSIG_CONSOLE_LOG_SAMPLE_RATE,
    'REACT_APP_STATSIG_CONSOLE_LOG_SAMPLE_RATE',
  );

  if (sampleRate != null && (sampleRate < 0 || sampleRate > 1)) {
    throw new Error(
      'REACT_APP_STATSIG_CONSOLE_LOG_SAMPLE_RATE must be between 0 and 1 when provided.',
    );
  }

  return {
    enabled: true,
    logLevel:
      parseOptionalConsoleLogLevel(env.REACT_APP_STATSIG_CONSOLE_LOG_LEVEL) ??
      DEFAULT_STATSIG_CONSOLE_LOG_CAPTURE_SETTINGS.logLevel,
    sampleRate:
      sampleRate ?? DEFAULT_STATSIG_CONSOLE_LOG_CAPTURE_SETTINGS.sampleRate,
    maxKeys:
      parseOptionalPositiveInteger(
        env.REACT_APP_STATSIG_CONSOLE_LOG_MAX_KEYS,
        'REACT_APP_STATSIG_CONSOLE_LOG_MAX_KEYS',
      ) ?? DEFAULT_STATSIG_CONSOLE_LOG_CAPTURE_SETTINGS.maxKeys,
    maxDepth:
      parseOptionalPositiveInteger(
        env.REACT_APP_STATSIG_CONSOLE_LOG_MAX_DEPTH,
        'REACT_APP_STATSIG_CONSOLE_LOG_MAX_DEPTH',
      ) ?? DEFAULT_STATSIG_CONSOLE_LOG_CAPTURE_SETTINGS.maxDepth,
    maxStringLength:
      parseOptionalPositiveInteger(
        env.REACT_APP_STATSIG_CONSOLE_LOG_MAX_STRING_LENGTH,
        'REACT_APP_STATSIG_CONSOLE_LOG_MAX_STRING_LENGTH',
      ) ?? DEFAULT_STATSIG_CONSOLE_LOG_CAPTURE_SETTINGS.maxStringLength,
  };
};
